import React, { useMemo, useState } from "react";
import toast from "react-hot-toast";
import {
    Box, Button, Drawer, DrawerBody, DrawerCloseButton, DrawerContent, DrawerFooter,
    DrawerHeader, DrawerOverlay, Flex, Icon, Stack, Text, useColorModeValue,
} from "@chakra-ui/react";
import { MdRestartAlt, MdLocalAtm } from "react-icons/md";
import { retryRefund } from "./paymentsApi";
import { REFUND_MAX_RETRY } from "./paymentsConstants";
import RefundsHistoryList from "./RefundsHistoryList";
import MarkRefundedDialog from "./MarkRefundedDialog";
import PaymentProviderChip from "./PaymentProviderChip";

/**
 * Renders a single key/value row.
 * @param {Object} props - { label, value }
 * @returns {React.ReactElement}
 */
function MetaRow({ label, value }) {
    const labelColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    const valueColor = useColorModeValue("secondaryGray.900", "white");
    return (
        <Flex justify="space-between" py="6px" gap="12px">
            <Text fontSize="sm" color={labelColor}>{label}</Text>
            <Text fontSize="sm" color={valueColor} fontWeight="600" textAlign="right">{value ?? "—"}</Text>
        </Flex>
    );
}

/**
 * Builds the metadata block from a payment object.
 * @param {Object} payment - Payment object
 * @returns {Array} Array of {label, value} entries
 */
function buildMetaRows(payment) {
    if (!payment) return [];
    return [
        { label: "ID paiement", value: `#${payment.id}` },
        { label: "Référence", value: payment.reference || "—" },
        { label: "Méthode", value: payment.payment_method_type || "—" },
        { label: "PaymentIntent", value: payment.payment_intent_id || "—" },
        { label: "Montant total", value: payment.total_amount ?? "—" },
        { label: "Déjà remboursé", value: payment.refunded ?? 0 },
        { label: "Tentatives", value: `${payment.refund_attempts ?? 0} / ${REFUND_MAX_RETRY}` },
        { label: "Réservation", value: payment.reservation_id ? `#${payment.reservation_id}` : "—" },
    ];
}

/**
 * Renders a row showing the provider chip next to a label.
 * @param {Object} props - { payment }
 * @returns {React.ReactElement}
 */
function ProviderRow({ payment }) {
    const labelColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    return (
        <Flex justify="space-between" align="center" py="6px" gap="12px">
            <Text fontSize="sm" color={labelColor}>Canal</Text>
            <PaymentProviderChip payment={payment} />
        </Flex>
    );
}

/**
 * Renders the metadata section. The provider chip appears right after
 * the "ID paiement" row to make the Stripe vs Orange Money split obvious.
 * @param {Object} props - { payment }
 * @returns {React.ReactElement}
 */
function MetaSection({ payment }) {
    const titleColor = useColorModeValue("secondaryGray.900", "white");
    const rows = buildMetaRows(payment);
    const [idRow, ...restRows] = rows;
    return (
        <Box>
            <Text fontSize="sm" fontWeight="700" color={titleColor} mb="8px">Détails du paiement</Text>
            {idRow && <MetaRow key={idRow.label} label={idRow.label} value={idRow.value} />}
            <ProviderRow payment={payment} />
            {restRows.map((r) => <MetaRow key={r.label} label={r.label} value={r.value} />)}
        </Box>
    );
}

/**
 * Triggers a Stripe retry. Counts toward the retry budget.
 * @param {Object} args - { paymentId, setBusy, onChanged }
 */
async function runRetry({ paymentId, setBusy, onChanged }) {
    setBusy(true);
    try {
        await retryRefund(paymentId);
        toast.success("Retry programmé — vérifiez l'historique dans quelques secondes");
        onChanged?.();
    } catch (error) {
        const msg = error?.response?.data || "Échec du retry";
        toast.error(typeof msg === "string" ? msg : "Échec du retry");
    } finally {
        setBusy(false);
    }
}

/**
 * Renders the action buttons. The retry button is hidden when no
 * payment_intent_id is present (Orange Money): no Stripe refund endpoint to call.
 * @param {Object} props - { onRetry, onOpenMark, busy, showRetry }
 * @returns {React.ReactElement}
 */
function ActionsRow({ onRetry, onOpenMark, busy, showRetry }) {
    return (
        <Stack direction={{ base: "column", sm: "row" }} spacing="12px" w="100%">
            {showRetry && (
                <Button onClick={onRetry} isLoading={busy} loadingText="Retry en cours..."
                    leftIcon={<Icon as={MdRestartAlt} />} colorScheme="brand" w="100%">Forcer un retry Stripe</Button>
            )}
            <Button onClick={onOpenMark} isDisabled={busy} leftIcon={<Icon as={MdLocalAtm} />}
                colorScheme="red" w="100%">Marquer comme remboursé</Button>
        </Stack>
    );
}

/**
 * Renders the drawer header with payment id and email.
 * @param {Object} props - { payment }
 * @returns {React.ReactElement}
 */
function RefundDrawerHeader({ payment }) {
    const subColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    return (
        <Box>
            <Text fontSize="md" fontWeight="700">Paiement #{payment?.id}</Text>
            <Text fontSize="xs" color={subColor} mt="2px">{payment?.email || "—"}</Text>
        </Box>
    );
}

/**
 * Picks the payment matching paymentId from the refunds list (memoized).
 * @param {number|null} paymentId - Selected payment ID
 * @param {Array} refunds - List of refunds from parent
 * @returns {Object|null}
 */
function useSelectedPayment(paymentId, refunds) {
    return useMemo(() => {
        if (!paymentId || !Array.isArray(refunds)) return null;
        return refunds.find((r) => r?.id === paymentId) || null;
    }, [paymentId, refunds]);
}

/**
 * Right-side drawer showing payment metadata, refund attempts history,
 * and the two action buttons (retry + mark refunded).
 * @param {Object} props - { paymentId, refunds, onClose, onChanged }
 * @returns {React.ReactElement}
 */
function RefundDetailDrawer({ paymentId, refunds, onClose, onChanged }) {
    const payment = useSelectedPayment(paymentId, refunds);
    const [busy, setBusy] = useState(false);
    const [markOpen, setMarkOpen] = useState(false);
    const onRetry = () => runRetry({ paymentId, setBusy, onChanged });
    const onMarked = () => { setMarkOpen(false); onChanged?.(); };
    return (
        <Drawer isOpen={Boolean(paymentId)} placement="right" onClose={onClose} size="md">
            <DrawerOverlay />
            <DrawerContent>
                <DrawerCloseButton />
                <DrawerHeader>{payment && <RefundDrawerHeader payment={payment} />}</DrawerHeader>
                <DrawerBody>
                    {payment && (
                        <Stack spacing="20px">
                            <MetaSection payment={payment} />
                            <RefundsHistoryList paymentId={paymentId} />
                        </Stack>
                    )}
                    {!payment && paymentId && (
                        <Text fontSize="sm" color="secondaryGray.600">Paiement introuvable dans la liste actuelle</Text>
                    )}
                </DrawerBody>
                {payment && (
                    <DrawerFooter>
                        <ActionsRow onRetry={onRetry} onOpenMark={() => setMarkOpen(true)} busy={busy}
                            showRetry={Boolean(payment?.payment_intent_id)} />
                    </DrawerFooter>
                )}
            </DrawerContent>
            <MarkRefundedDialog open={markOpen} payment={payment}
                onClose={() => setMarkOpen(false)} onMarked={onMarked} />
        </Drawer>
    );
}

export default RefundDetailDrawer;
